import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import Navbar from '../components/Navbar.jsx';
import styles from './ProductCatalog.module.css';
import PromotionsDisplay from '../components/PromotionsDisplay';

const ProductCatalog = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentCategory, setCurrentCategory] = useState('all');
    const [currentSort, setCurrentSort] = useState('createdAt:desc');
    const [currentMaxPrice, setCurrentMaxPrice] = useState(200000);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFlags, setSelectedFlags] = useState([]);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [debugMode, setDebugMode] = useState(false);
    
    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [productsPerPage] = useState(6); // 6 products per page
    
    const { addToCart, getCartCount } = useCart();
    const [cartCount, setCartCount] = useState(0);
    
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    // Calculate pagination
    const indexOfLastProduct = currentPage * productsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
    const currentProducts = products.slice(indexOfFirstProduct, indexOfLastProduct);
    const totalPages = Math.ceil(products.length / productsPerPage);

    // Pagination functions
    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const nextPage = () => {
        if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
    };
    const prevPage = () => {
        if (currentPage > 1) setCurrentPage(prev => prev - 1);
    };

    useEffect(() => {
        const urlCategory = searchParams.get('category');
        if (urlCategory) {
            setCurrentCategory(urlCategory);
        }
        
        fetchCategories();
        fetchProducts();
    }, [currentCategory, currentSort, currentMaxPrice, searchQuery, selectedFlags]);

    useEffect(() => {
        setCartCount(getCartCount());
    }, [getCartCount]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [currentCategory, currentSort, currentMaxPrice, searchQuery, selectedFlags]);

    // Listen for inventory updates
    useEffect(() => {
        const handleInventoryUpdate = () => {
            console.log('🔄 Inventory update detected, refreshing products...');
            fetchProducts();
        };

        const handleRefreshAllProducts = () => {
            console.log('🔄 Refreshing all products...');
            fetchProducts();
        };

        window.addEventListener('inventoryUpdated', handleInventoryUpdate);
        window.addEventListener('refreshAllProducts', handleRefreshAllProducts);
        
        return () => {
            window.removeEventListener('inventoryUpdated', handleInventoryUpdate);
            window.removeEventListener('refreshAllProducts', handleRefreshAllProducts);
        };
    }, []);
    
    // Fetch categories from backend
    const fetchCategories = async () => {
        try {
            console.log(' Fetching categories from:', `${API_URL}/categories`);
            
            const response = await fetch(`${API_URL}/categories`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                signal: AbortSignal.timeout(10000) 
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log(' Categories API response:', data);
            
            // Handle different response formats
            let categoriesArray = [];
            
            if (Array.isArray(data)) {
                categoriesArray = data;
            } else if (data && Array.isArray(data.categories)) {
                categoriesArray = data.categories;
            } else if (data && Array.isArray(data.data)) {
                categoriesArray = data.data;
            } else if (data && typeof data === 'object') {
                categoriesArray = Object.values(data).filter(item => 
                    item && typeof item === 'object' && item.name
                );
            }
            
            console.log(' Extracted categories array:', categoriesArray);
            
            // Get main categories (level 1) or all categories if no level specified
            const mainCategories = categoriesArray
                .filter(cat => cat && cat.name) 
                .filter(cat => cat.level === 1 || !cat.level);
                
            console.log(' Main categories to display:', mainCategories);
            setCategories(mainCategories);
            
        } catch (error) {
            console.error(' Error fetching categories:', error);
            
            if (error.name === 'TimeoutError') {
                setError('Categories server is taking too long to respond. Please check if the backend is running.');
            } else if (error.message.includes('Failed to fetch')) {
                setError('Cannot connect to categories server. Please make sure the backend is running on http://localhost:5000');
            } else {
                setError(`Error loading categories: ${error.message}`);
            }
            
            setCategories([]); 
        }
    };

    // Fetch products
    const fetchProducts = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const params = new URLSearchParams();

            // Add search filter
            if (searchQuery.trim()) {
                params.append('search', searchQuery.trim());
            }

            // Add price filter
            params.append('maxPrice', currentMaxPrice);
            
            // Add sort
            const [sortField, sortDirection] = currentSort.split(':');
            params.append('sort', `${sortField}:${sortDirection}`);

            // Add flags
            selectedFlags.forEach(flag => {
                params.append(flag, 'true');
            });

            const queryString = params.toString();
            const url = `${API_URL}/products?${queryString}`;

            console.log('Fetching products from:', url);
            
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`Failed to fetch products: ${response.status}`);
            }
            
            const data = await response.json();
            let productsData = data.products || data || [];
            
            // Filter by category on client side
            if (currentCategory !== 'all') {
                productsData = productsData.filter(product => {
                    const productCategory = product.category?.name || product.category;
                    return productCategory?.toLowerCase().includes(currentCategory.toLowerCase());
                });
            }
            
            console.log('Fetched products:', productsData.length);
            
            // Debug: Log inventory data for all products
            if (debugMode) {
                console.log('🧪 INVENTORY DEBUG FOR ALL PRODUCTS:');
                productsData.forEach(product => {
                    const availableStock = product.inventory ? 
                        (product.inventory.stock - (product.inventory.reservedStock || 0)) : 0;
                    
                    console.log(`📦 ${product.name}:`, {
                        stock: product.inventory?.stock,
                        reserved: product.inventory?.reservedStock,
                        available: availableStock
                    });
                });
            }
            
            setProducts(productsData);
            
        } catch (error) {
            console.error('Error fetching products:', error);
            setError('Failed to load products. Please check if the server is running.');
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle category selection
    const handleCategoryClick = (categoryName) => {
        if (categoryName === 'all') {
            setCurrentCategory('all');
            navigate('/products', { replace: true });
        } else {
            const categorySlug = categoryName.toLowerCase().replace(/\s+/g, '-');
            navigate(`/categories/${categorySlug}`, { replace: true });
        }
        setIsSidebarOpen(false);
    };

    // Handle search
    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        fetchProducts();
    };

    // Handle price filter change
    const handlePriceFilterChange = (value) => {
        const priceValue = parseInt(value);
        setCurrentMaxPrice(priceValue);
    };

    // Handle price input change
    const handlePriceInputChange = (e) => {
        let value = parseInt(e.target.value) || 0;
        if (value > 200000) value = 200000;
        if (value < 0) value = 0;
        setCurrentMaxPrice(value);
    };

    // Handle flag filters
    const toggleFlagFilter = (flag) => {
        setSelectedFlags(prev => 
            prev.includes(flag) 
                ? prev.filter(f => f !== flag)
                : [...prev, flag]
        );
    };

    // Toggle sidebar for mobile
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    // Toggle sidebar collapse for desktop
    const toggleSidebarCollapse = () => {
        setSidebarCollapsed(!sidebarCollapsed);
    };

    // Debug function to test inventory data
    const testInventoryDebug = async () => {
        console.log('🧪 MANUAL INVENTORY DEBUG TEST');
        
        products.forEach(product => {
            const availableStock = product.inventory ? 
                (product.inventory.stock - (product.inventory.reservedStock || 0)) : 0;
            
            console.log('📦 PRODUCT INVENTORY:', {
                name: product.name,
                id: product._id,
                stock: product.inventory?.stock,
                reservedStock: product.inventory?.reservedStock,
                availableStock: availableStock,
                showsInStock: product.inventory?.stock > 0,
                shouldShowAvailable: availableStock > 0
            });
        });
        
        alert('Check browser console for inventory debug info!');
    };

    // Check authentication before adding to cart
    const handleAddToCart = (product) => {
        // Check if user is authenticated
        const user = localStorage.getItem('bloomsoko-user');
        if (!user) {
            navigate('/login', { 
                state: { from: '/products' },
                replace: true 
            });
            return;
        }

        const categoryName = product.category?.name || product.category || 'Uncategorized';
        const isFarmProduct = categoryName.toLowerCase().includes('agriculture') || 
                            categoryName.toLowerCase().includes('farm');
        const isReady = product.productType === 'ready';
        
        const shouldBook = isFarmProduct && !isReady;
        
        addToCart(product, 1, shouldBook);
        
        // Refresh products to get updated inventory
        setTimeout(() => {
            fetchProducts();
        }, 500);
        
        if (shouldBook) {
            alert(`📅 ${product.name} booked successfully! We'll contact you when ready.`);
        } else {
            alert(`🛒 ${product.name} added to cart!`);
        }
    };

    // Get category class for styling
    const getCategoryClass = (categoryName) => {
        const categoryMap = {
            'Agricultural Produce': 'farm',
            'Agriculture': 'farm',
            'Beauty': 'cosmetics',
            'Fashion': 'fashion',
            'Household': 'home',
            'House & Garden': 'home'
        };
        return categoryMap[categoryName] || 'farm';
    };

    // Get product badges
    const getProductBadges = (product) => {
        const badges = [];
        
        // Calculate available stock for badge logic
        const availableStock = product.inventory ? 
            (product.inventory.stock - (product.inventory.reservedStock || 0)) : 0;
        
        if (availableStock <= 0) {
            badges.push({ text: 'Out of Stock', class: 'outOfStock' });
        }
        
        if (product.productType === 'growing') {
            badges.push({ text: '🌱 Growing', class: 'growing' });
        } else if (product.productType === 'pre-order') {
            badges.push({ text: '📅 Pre-order', class: 'preOrder' });
        }
        
        // Updated badge priorities
        if (product.flags?.isNew) badges.push({ text: '🆕 New', class: 'new' });
        if (product.flags?.isLimited) badges.push({ text: '⏰ Limited', class: 'limited' });
        if (product.flags?.onSale) badges.push({ text: '🏷️ Offer', class: 'sale' });
        if (product.flags?.isBestSeller) badges.push({ text: '🔥 Best Seller', class: 'bestSeller' });
        
        return badges.slice(0, 2);
    };

    // Get growing progress for growing products
    const getGrowingProgress = (product) => {
        if (product.productType === 'ready') return null;
        
        const progress = product.growingDetails?.progress || 50;
        const stage = product.growingDetails?.currentStage || 'growing';
        
        const stageLabels = {
            'planting': 'Planting',
            'growing': ' Growing', 
            'almost-ready': ' Almost Ready',
            'ready': ' Ready'
        };
        
        return {
            label: stageLabels[stage] || ' Growing',
            progress: progress
        };
    };

    // Clear all filters
    const clearAllFilters = () => {
        setCurrentCategory('all');
        setCurrentMaxPrice(200000);
        setSelectedFlags([]);
        setSearchQuery('');
        setCurrentPage(1);
        navigate('/products', { replace: true });
    };

    return (
        <div className={styles.productCatalog}>
            {/* Debug Toggle Button */}
            {debugMode && (
                <div style={{
                    position: 'fixed',
                    top: '10px',
                    right: '10px',
                    zIndex: 1000,
                    background: 'red',
                    color: 'white',
                    padding: '10px 15px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.3)'
                }} onClick={testInventoryDebug}>
                    🧪 Debug Inventory
                </div>
            )}

            <Navbar />

            {/* Desktop Sidebar Toggle Button */}
            <button 
                className={styles.desktopSidebarToggle}
                onClick={toggleSidebarCollapse}
                title={sidebarCollapsed ? "Show Filters" : "Hide Filters"}
            >
                {sidebarCollapsed ? "›" : "‹"}
            </button>

            {/* Mobile Sidebar Toggle */}
            <button className={styles.mobileSidebarToggle} onClick={toggleSidebar}>
                ☰ Filters
            </button>

            {isSidebarOpen && (
                <div className={styles.sidebarOverlay} onClick={toggleSidebar}></div>
            )}

            {/* Top Search Bar */}
            <div className={styles.topSearchBar}>
                <form onSubmit={handleSearchSubmit} className={styles.searchForm}>
                    <input 
                        type="text" 
                        placeholder="Search products by name, description, or tags..." 
                        className={styles.searchInput}
                        value={searchQuery}
                        onChange={handleSearch}
                    />
                    <button type="submit" className={styles.searchButton}>
                        🔍 Search
                    </button>
                </form>
            </div>

            {/*  PROMOTIONS */}
            <PromotionsDisplay position="top_banner" />

            {/* Header */}
            <div className={styles.shopHeader}>
                <h1>
                    {currentCategory === 'all' ? 'Shop with us' : currentCategory}
                    
                </h1>
                <div className={styles.breadcrumbs}>
                    <Link to="/">Home</Link> &gt; 
                    <Link to="/products">Products</Link> &gt; 
                    <span>{currentCategory === 'all' ? 'All Categories' : currentCategory}</span>
                </div>
            </div>
            
            {/* Main Content */}
            <div className={styles.shopContainer}>
                {/* Sidebar Filters */}
                <aside className={`${styles.filters} ${isSidebarOpen ? styles.sidebarOpen : ''} ${sidebarCollapsed ? styles.hidden : ''}`}>
                    <div className={styles.sidebarHeader}>
                        <h3>Filters & Categories</h3>
                        <button className={styles.closeSidebar} onClick={toggleSidebar}>×</button>
                        <button className={styles.closeSidebarDesktop} onClick={toggleSidebarCollapse}>×</button>
                    </div>
                    
                    {/* Categories Section */}
                    <div className={styles.filterSection}>
                        <h3>Categories</h3>
                        <ul className={styles.categoryList}>
                            <li>
                                <button 
                                    className={currentCategory === 'all' ? styles.active : ''}
                                    onClick={() => handleCategoryClick('all')}
                                >
                                    All Products
                                </button>
                            </li>
                            {categories.map(category => (
                                <li key={category._id || category.id || category.name}>
                                    <button 
                                        className={currentCategory === category.name ? styles.active : ''}
                                        onClick={() => handleCategoryClick(category.name)}
                                    >
                                        {category.name}
                                    </button>
                                </li>
                            ))}
                        </ul>
                        {categories.length === 0 && !loading && (
                            <p className={styles.noCategories}>No categories available</p>
                        )}
                    </div>
                    
                    {/* Product Features */}
                    <div className={styles.filterSection}>
                        <h3>Product Features</h3>
                        <div className={styles.flagFilters}>
                            <label className={styles.flagOption}>
                                <input
                                    type="checkbox"
                                    checked={selectedFlags.includes('isNew')}
                                    onChange={() => toggleFlagFilter('isNew')}
                                />
                                <span>🆕 New Arrivals</span>
                            </label>
                            <label className={styles.flagOption}>
                                <input
                                    type="checkbox"
                                    checked={selectedFlags.includes('isLimited')}
                                    onChange={() => toggleFlagFilter('isLimited')}
                                />
                                <span>⏰ Limited Edition</span>
                            </label>
                            <label className={styles.flagOption}>
                                <input
                                    type="checkbox"
                                    checked={selectedFlags.includes('onSale')}
                                    onChange={() => toggleFlagFilter('onSale')}
                                />
                                <span>🏷️ On Offer</span>
                            </label>
                            <label className={styles.flagOption}>
                                <input
                                    type="checkbox"
                                    checked={selectedFlags.includes('isBestSeller')}
                                    onChange={() => toggleFlagFilter('isBestSeller')}
                                />
                                <span>🔥 Best Seller</span>
                            </label>
                        </div>
                    </div>

                    {/* Price Range */}
                    <div className={styles.filterSection}>
                        <h3>Price Range</h3>
                        <div className={styles.priceRangeContainer}>
                            <input 
                                type="range" 
                                min="0" 
                                max="200000" 
                                value={currentMaxPrice} 
                                className={styles.priceRange} 
                                onChange={(e) => handlePriceFilterChange(e.target.value)}
                            />
                            <div className={styles.priceInputGroup}>
                                <input 
                                    type="number" 
                                    min="0" 
                                    max="200000" 
                                    value={currentMaxPrice} 
                                    className={styles.priceInput} 
                                    onChange={handlePriceInputChange}
                                    placeholder="Enter max price"
                                />
                            </div>
                            <div className={styles.priceValues}>
                                <span>KSh 0</span>
                                <span>KSh {currentMaxPrice.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Sort Options */}
                    <div className={styles.filterSection}>
                        <h3>Sort By</h3>
                        <select 
                            className={styles.sortFilter} 
                            value={currentSort}
                            onChange={(e) => setCurrentSort(e.target.value)}
                        >
                            <option value="createdAt:desc">Newest Arrivals</option>
                            <option value="price:asc">Price: Low to High</option>
                            <option value="price:desc">Price: High to Low</option>
                            <option value="name:asc">Name: A-Z</option>
                            <option value="flags.isFeatured:desc">Featured First</option>
                        </select>
                    </div>

                    {/* Active Filters */}
                    {(selectedFlags.length > 0 || currentCategory !== 'all' || currentMaxPrice < 200000) && (
                        <div className={styles.filterSection}>
                            <h3>Active Filters</h3>
                            <div className={styles.activeFilters}>
                                {currentCategory !== 'all' && (
                                    <span className={styles.activeFilter}>
                                        Category: {currentCategory}
                                        <button onClick={() => handleCategoryClick('all')}>×</button>
                                    </span>
                                )}
                                {currentMaxPrice < 200000 && (
                                    <span className={styles.activeFilter}>
                                        Max: KSh {currentMaxPrice.toLocaleString()}
                                        <button onClick={() => setCurrentMaxPrice(200000)}>×</button>
                                    </span>
                                )}
                                {selectedFlags.map(flag => (
                                    <span key={flag} className={styles.activeFilter}>
                                        {flag.replace('is', '').replace(/([A-Z])/g, ' $1').trim()}
                                        <button onClick={() => toggleFlagFilter(flag)}>×</button>
                                    </span>
                                ))}
                                <button 
                                    className={styles.clearAll}
                                    onClick={clearAllFilters}
                                >
                                    Clear All Filters
                                </button>
                            </div>
                        </div>
                    )}
                    <PromotionsDisplay position="sidebar" />
                </aside>
                
                {/* Products Grid */}
                <main className={styles.productGrid}>
                    {/* Pagination Info */}
                    <div className={styles.paginationInfo}>
                        
                        {totalPages > 1 && (
                            <span className={styles.pageInfo}>
                                Page {currentPage} of {totalPages}
                            </span>
                        )}
                    </div>

                    {loading ? (
                        <div className={styles.loading}>
                            <div className={styles.loadingSpinner}></div>
                            Loading products...
                        </div>
                    ) : error ? (
                        <div className={styles.errorMessage}>
                            <p>{error}</p>
                            <p>Please make sure your backend server is running on http://localhost:5000</p>
                        </div>
                    ) : products.length === 0 ? (
                        <div className={styles.noProducts}>
                            <h3>No products found</h3>
                            <p>Try adjusting your filters or search terms</p>
                            <button 
                                className={styles.ctaSecondary}
                                onClick={clearAllFilters}
                            >
                                Clear All Filters
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className={styles.productsList}>
                                {currentProducts.map(product => {
                                    const categoryName = product.category?.name || product.category || 'Uncategorized';
                                    const categoryClass = getCategoryClass(categoryName);
                                    const isFarmProduct = categoryName.toLowerCase().includes('agriculture') || 
                                                        categoryName.toLowerCase().includes('farm');
                                    const isReady = product.productType === 'ready';
                                    const buttonText = isFarmProduct && !isReady ? 'Book Now' : 'Add to Cart';
                                    const badges = getProductBadges(product);
                                    const growingProgress = getGrowingProgress(product);

                                    // Calculate available stock properly
                                    const availableStock = product.inventory ? 
                                        (product.inventory.stock - (product.inventory.reservedStock || 0)) : 0;

                                    return (
                                        <div key={product._id} className={styles.productCard}>
                                            <div 
                                                className={styles.productImage} 
                                                style={{
                                                    backgroundImage: product.featuredImage?.url || product.images?.[0]?.url ? 
                                                        `url('${product.featuredImage?.url || product.images?.[0]?.url}')` : 'none',
                                                    backgroundColor: !product.featuredImage?.url && !product.images?.[0]?.url ? '#f8f9fa' : 'transparent'
                                                }}
                                            >
                                                <div className={styles.badgeContainer}>
                                                    {badges.map((badge, index) => (
                                                        <span key={index} className={`${styles.productBadge} ${styles[badge.class]}`}>
                                                            {badge.text}
                                                        </span>
                                                    ))}
                                                </div>
                                                
                                                {!product.featuredImage?.url && !product.images?.[0]?.url && (
                                                    <div className={styles.noImagePlaceholder}>
                                                        No Image
                                                    </div>
                                                )}
                                            </div>

                                            <div className={styles.productInfo}>
                                                <div className={styles.categoryTag}>
                                                    {categoryName}
                                                </div>

                                                <h3 className={styles.productTitle}>{product.name}</h3>
                                                
                                                <p className={styles.productDescription}>
                                                    {product.description?.substring(0, 80)}...
                                                </p>

                                                {product.tags && product.tags.length > 0 && (
                                                    <div className={styles.productTags}>
                                                        {product.tags.slice(0, 3).map((tag, index) => (
                                                            <span key={index} className={styles.tag}>
                                                                #{tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}

                                                {growingProgress && (
                                                    <div className={styles.growingProgress}>
                                                        <div className={styles.progressLabel}>
                                                            {growingProgress.label} • {growingProgress.progress}%
                                                        </div>
                                                        <div className={styles.progressBar}>
                                                            <div 
                                                                className={styles.progressFill}
                                                                style={{ width: `${growingProgress.progress}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className={styles.priceSection}>
                                                    <span className={`${styles.currentPrice} ${styles['price' + categoryClass.charAt(0).toUpperCase() + categoryClass.slice(1)]}`}>
                                                        KSh {product.price?.toLocaleString()}
                                                    </span>
                                                    {product.comparePrice > product.price && (
                                                        <span className={styles.comparePrice}>
                                                            KSh {product.comparePrice?.toLocaleString()}
                                                        </span>
                                                    )}
                                                    {product.flags?.onSale && (
                                                        <span className={styles.discountBadge}>
                                                            Save {Math.round((1 - product.price / product.comparePrice) * 100)}%
                                                        </span>
                                                    )}
                                                </div>

                                                <div className={styles.stockInfo}>
                                                    {isReady ? (
                                                        <>
                                                            <span className={`${styles.stockStatus} ${availableStock > 0 ? styles.inStock : styles.outOfStock}`}>
                                                                {availableStock > 0 ? 'In Stock' : 'Out of Stock'}
                                                            </span>
                                                            {availableStock > 0 && (
                                                                <span className={styles.stockQuantity}>
                                                                    {availableStock} available
                                                                    {/* Show detailed inventory info in debug mode */}
                                                                    {debugMode && product.inventory?.reservedStock > 0 && (
                                                                        <span style={{
                                                                            fontSize: '0.7em', 
                                                                            color: '#666', 
                                                                            display: 'block',
                                                                            fontStyle: 'italic'
                                                                        }}>
                                                                            ({product.inventory.stock} total - {product.inventory.reservedStock} reserved)
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <span className={styles.preOrderStatus}>
                                                            Pre-order Available
                                                        </span>
                                                    )}
                                                </div>

                                                <button 
                                                    className={`${styles.addToCart} ${styles['btn' + categoryClass.charAt(0).toUpperCase() + categoryClass.slice(1)]}`}
                                                    onClick={() => handleAddToCart(product)}
                                                    disabled={availableStock === 0}
                                                >
                                                    {availableStock === 0 ? 'Out of Stock' : buttonText}
                                                </button>

                                                <Link 
                                                    to={`/product/${product._id}`} 
                                                    className={styles.quickView}
                                                >
                                                    View Details →
                                                </Link>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div className={styles.pagination}>
                                    <button 
                                        className={styles.pageButton}
                                        onClick={prevPage}
                                        disabled={currentPage === 1}
                                    >
                                        ‹ Previous
                                    </button>
                                    
                                    <div className={styles.pageNumbers}>
                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                                            <button
                                                key={number}
                                                className={`${styles.pageNumber} ${currentPage === number ? styles.active : ''}`}
                                                onClick={() => paginate(number)}
                                            >
                                                {number}
                                            </button>
                                        ))}
                                    </div>
                                    
                                    <button 
                                        className={styles.pageButton}
                                        onClick={nextPage}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next ›
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                    <PromotionsDisplay position="product_page" />
                </main>
            </div>
        </div>
    );
};


export default ProductCatalog;